import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import { useAuth } from "../AuthProvider";
import { syncServerClock } from "../utils/serverClock";

export type RealtimeEvent = {
  event: string;
  data?: Record<string, any>;
};

type Subscriber = (event: RealtimeEvent) => void;
type LiveError = { status: number; code: string; message: string };
type LiveSubscriber = {
  resource: string;
  params: Record<string, unknown>;
  onData: (payload: unknown) => void;
  onError?: (error: LiveError) => void;
};

/**
 * The close code the server uses when it has ended this session on purpose.
 *
 * Distinct from every transport failure, so the client can tell "your session
 * is over" from "the network blinked" — the two want opposite responses.
 */
const SESSION_ENDED_CLOSE_CODE = 4001;

const getWebSocketUrl = (token: string | null) => {
  const baseUrl = process.env.REACT_APP_WS_URL || "ws://localhost:4000/ws";
  if (!token) return baseUrl;
  const separator = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${separator}token=${encodeURIComponent(token)}`;
};

const RealtimeContext = createContext<{
  subscribe: (subscriber: Subscriber) => () => void;
  watch: (
    resource: string,
    params: Record<string, unknown>,
    onData: (payload: unknown) => void,
    onError?: (error: LiveError) => void
  ) => () => void;
} | null>(null);

/*
  One socket for the whole signed-in session.

  It used to be opened by the hook itself, so every page that wanted events
  opened its own, and navigating between them tore one down to build another.
  The connection now lives here and pages subscribe to it; a page that mounts,
  unmounts and mounts again — which StrictMode does to all of them in
  development — adds and removes a callback rather than a socket.
*/
export const RealtimeProvider = ({ children }: { children: ReactNode }) => {
  const { token } = useAuth();
  const subscribers = useRef(new Set<Subscriber>());
  const liveSubscribers = useRef(new Map<string, LiveSubscriber>());
  const socketRef = useRef<WebSocket | null>(null);
  const nextLiveId = useRef(0);

  const send = useCallback((message: unknown) => {
    if (socketRef.current?.readyState !== WebSocket.OPEN) return;
    socketRef.current.send(JSON.stringify(message));
  }, []);

  const subscribe = useCallback((subscriber: Subscriber) => {
    subscribers.current.add(subscriber);
    return () => {
      subscribers.current.delete(subscriber);
    };
  }, []);

  const watch = useCallback(
    (
      resource: string,
      params: Record<string, unknown>,
      onData: (payload: unknown) => void,
      onError?: (error: LiveError) => void
    ) => {
      const id = `player-${++nextLiveId.current}`;
      liveSubscribers.current.set(id, { resource, params, onData, onError });
      send({ type: "subscribe", id, resource, params });
      return () => {
        liveSubscribers.current.delete(id);
        send({ type: "unsubscribe", id });
      };
    },
    [send]
  );

  useEffect(() => {
    let isClosed = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: number | undefined;
    let syncTimer: number | undefined;

    const sendSubscriptions = () => {
      liveSubscribers.current.forEach((subscription, id) => {
        send({
          type: "subscribe",
          id,
          resource: subscription.resource,
          params: subscription.params,
        });
      });
    };

    const connect = () => {
      if (isClosed) return;
      socket = new WebSocket(getWebSocketUrl(token));
      socketRef.current = socket;
      socket.onmessage = (message) => {
        try {
          const event = JSON.parse(message.data) as RealtimeEvent & {
            data?: Record<string, any>;
            ts?: number;
          };
          syncServerClock(event.ts, event.event === "connected");

          if (event.event === "live.data") {
            const id = String(event.data?.id || "");
            liveSubscribers.current.get(id)?.onData(event.data?.payload);
            return;
          }

          if (event.event === "live.error") {
            const id = String(event.data?.id || "");
            liveSubscribers.current.get(id)?.onError?.({
              status: Number(event.data?.status || 0),
              code: String(event.data?.code || "LIVE_FAILED"),
              message: String(event.data?.message || "Could not refresh"),
            });
            return;
          }

          subscribers.current.forEach((subscriber) => subscriber(event));
        } catch (error) {
          console.error("Realtime message error:", error);
        }
      };
      socket.onopen = () => {
        sendSubscriptions();
        syncTimer = window.setInterval(() => {
          if (liveSubscribers.current.size) send({ type: "sync" });
        }, 10_000);
      };
      socket.onclose = (event) => {
        if (syncTimer !== undefined) window.clearInterval(syncTimer);
        /*
         * 4001 is the server saying this session is over — the account signed
         * in somewhere else, and one account holds one session. Reconnecting
         * would reopen on a token it has already refused, every three seconds,
         * for as long as the tab stays open.
         */
        if (event.code === SESSION_ENDED_CLOSE_CODE) return;
        if (!isClosed) reconnectTimer = window.setTimeout(connect, 3000);
      };
    };

    connect();

    return () => {
      isClosed = true;
      if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer);
      if (syncTimer !== undefined) window.clearInterval(syncTimer);
      if (socket) {
        socket.onmessage = null;
        socket.onopen = null;
        socket.onclose = null;
        socket.close();
      }
      if (socketRef.current === socket) socketRef.current = null;
    };
  }, [send, token]);

  return createElement(
    RealtimeContext.Provider,
    { value: { subscribe, watch } },
    children
  );
};

/** Subscribes to the session's socket. Opens nothing of its own. */
export const usePlayerRealtime = (onEvent: (event: RealtimeEvent) => void) => {
  const context = useContext(RealtimeContext);
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!context) return;
    return context.subscribe((event) => onEventRef.current(event));
  }, [context]);
};

export const usePlayerLive = <T,>(
  resource: string,
  params: Record<string, unknown>,
  onData: (payload: T) => void,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const context = useContext(RealtimeContext);
  const onDataRef = useRef(onData);
  const paramsKey = JSON.stringify(params);
  // The serialized key intentionally controls subscription identity by value.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableParams = useMemo(() => params, [paramsKey]);

  useEffect(() => {
    onDataRef.current = onData;
  }, [onData]);

  useEffect(() => {
    if (!context || !enabled) return;
    return context.watch(
      resource,
      stableParams,
      (payload) => onDataRef.current(payload as T),
      (error) => console.error(`Live ${resource} error:`, error)
    );
  }, [context, enabled, resource, stableParams]);
};
